/* eslint-disable react/no-unknown-property */
import React, { useRef, useMemo, useEffect, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useTheme } from "@/hooks";

const COLOR_LIGHT = "#ebeef4";
const COLOR_DARK = "#090a0d";

const fragmentShader = `
  uniform float u_progress;
  uniform float u_direction;
  uniform vec3 u_colorLight;
  uniform vec3 u_colorDark;
  uniform float u_time;
  uniform vec2 u_resolution;
  varying vec2 vUv;

  // Simple 2D pseudo-random
  float random(vec2 st) { 
    return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123); 
  }

  // 2D Noise
  float noise(vec2 st) {
      vec2 i = floor(st); 
      vec2 f = fract(st); 
      vec2 u = f*f*(3.0-2.0*f);
      return mix(mix(random(i), random(i+vec2(1.0,0.0)), u.x), 
                 mix(random(i+vec2(0.0,1.0)), random(i+vec2(1.0,1.0)), u.x), u.y);
  }

  void main() {
    // Preserve screen aspect ratio for the structural wave grid
    vec2 aspect = vec2(u_resolution.x / u_resolution.y, 1.0);
    vec2 uvAspect = vUv * aspect;

    vec2 offsetUv = uvAspect;

    // Continuous slowly mutating liquid noise layer
    float liquid = noise(offsetUv * 2.0 + u_time * 0.1) * 0.5 + 
                   noise(offsetUv * 4.0 - u_time * 0.15) * 0.25;

    // --- Transition State Masking ---
    // Extract base screen-spanning diagonal without aspect ratio distortion bounds (-1.0 to 1.0)
    // u_direction = 1.0 (Dark Mode): Top-Left to Bottom-Right
    // u_direction = 0.0 (Light Mode): Top-Right to Bottom-Left
    float baseTL = vUv.x - vUv.y; 
    float baseTR = vUv.x + vUv.y - 1.0; 
    float base = mix(baseTR, baseTL, u_direction);
    
    // Map u_progress (0 to 1) to an expanded sweeping bound (-3.0 to 2.0). 
    // This dramatically wider range guarantees the heavily distorted +1.2 liquid noise radius fully clears the -1.0 top-left screen corner!
    float sweepEdge = (u_progress * 5.0) - 3.0; 
    
    // Distort the linear color sweep edge heavily with the liquid noise
    float distortedEdge = sweepEdge + (liquid * 1.2);
    
    // Create a smooth transition boundary pixel interpolation
    float mixFactor = smoothstep(distortedEdge - 0.5, distortedEdge + 0.5, base);
    
    // If u_progress=0 (Light), mixFactor=1 -> evaluate u_colorLight at 1.0
    vec3 bgColor = mix(u_colorDark, u_colorLight, mixFactor);

    // Blend structural elements
    vec3 finalColor = bgColor + (liquid * 0.02);

    gl_FragColor = vec4(finalColor, 1.0);
  }
`;

const ShaderPlane = ({ mode }: { mode: string }) => {
  const materialRef = useRef<THREE.ShaderMaterial>(null);
  const isBrowser = typeof window !== "undefined";

  const uniforms = useMemo(
    () => ({
      u_progress: { value: mode === "dark" ? 1.0 : 0.0 },
      u_direction: { value: mode === "dark" ? 1.0 : 0.0 }, // 1.0 tracks TL wipe, 0.0 tracks TR wipe
      u_colorLight: { value: new THREE.Color(COLOR_LIGHT) },
      u_colorDark: { value: new THREE.Color(COLOR_DARK) },
      u_time: { value: 0.0 },
      u_resolution: {
        value: new THREE.Vector2(
          isBrowser ? window.innerWidth : 1000,
          isBrowser ? window.innerHeight : 1000,
        ),
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [],
  ); // initialize once

  const targetProgress = mode === "dark" ? 1.0 : 0.0;
  const targetDirection = mode === "dark" ? 1.0 : 0.0;

  useFrame((_state, delta) => {
    if (materialRef.current) {
      // Instantly swap the diagonal wipe coordinate matrix when the button is clicked!
      // This happens instantly while the entire screen is fully clamped, creating a seamless vector shift.
      materialRef.current.uniforms.u_direction.value = targetDirection;

      // Lerp u_progress towards the target state to drive the cinematic shader wipe
      // Slowed delta from 2.5 to 1.5 to compensate for the expanded 5.0 distance sweep bounds!
      const current = materialRef.current.uniforms.u_progress.value;
      materialRef.current.uniforms.u_progress.value = THREE.MathUtils.lerp(
        current,
        targetProgress,
        delta * 1.5,
      );

      // Accumulate time for continuous fluid motion
      materialRef.current.uniforms.u_time.value += delta;

      // Dynamically sync resolution
      materialRef.current.uniforms.u_resolution.value.set(
        window.innerWidth,
        window.innerHeight,
      );
    }
  });

  return (
    <mesh>
      <planeGeometry args={[2, 2]} />
      <shaderMaterial
        ref={materialRef}
        uniforms={uniforms}
        vertexShader={`
          varying vec2 vUv;
          void main() {
            vUv = uv;
            // Bypass the PerspectiveCamera and lock directly to screen NDC coordinates
            gl_Position = vec4(position, 1.0);
          }
        `}
        fragmentShader={fragmentShader}
      />
    </mesh>
  );
};

const ThemeWorldspace: React.FC = () => {
  const [{ mode }] = useTheme();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) return null; // Avoid Hydration Mismatch across SSR builds

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: -1,
        pointerEvents: "none",
      }}
    >
      <Canvas dpr={[1, 2]} orthographic>
        <ShaderPlane mode={mode} />
      </Canvas>
    </div>
  );
};

export default ThemeWorldspace;
